import { type AxialCoord, hexDistance, hexEquals, hexNeighbors } from "../hex";
import { buildStructure, isBuildable, nextImprovement } from "../land/build";
import { inCityReach, isCitySite, workingCities } from "../land/cities";
import { applyFirst, rankBy } from "../pick";
import { findHex, ownedBy, ownGround, unitsOf } from "../run/board";
import { kingOf, supplyOf } from "../run/pools";
import { scriptUnitsOf } from "../run/seats";
import { allied } from "../run/sides";
import type { FactionId, HexState, MayorStance, RunState, Unit } from "../types/state";
import { unitCount, unitRoom } from "../units/roster";
import { scriptedCommands } from "./armyScript";
import { commandersOf } from "./commanders";
import {
  awaitsAim,
  promotionCandidate,
  scriptedAims,
  scriptedBanner,
  scriptedCall,
  scriptedPromotions,
  waitsForBanner,
} from "./generalScript";
import {
  capitalStands,
  inKingReach,
  kingReach,
  mayoredCities,
  moveKing,
  stopKing,
  trust,
} from "./king";
import {
  appointGovernor,
  awaitsGovernor,
  developSites,
  mayorLand,
  mayorOf,
  setStance,
  stanceOf,
} from "./mayors";

/**
 * The court's script: what a scripted King commands each turn (stances, governors, promotions,
 * aims, calls, banners, builds, his own walk) through the same rules as a human; his army's
 * recruits and orders are ./armyScript.ts.
 */

/**
 * Where the script's King stands: the own hex from which it commands the most work, an idle
 * own unit counting 1, a working city with an idle yard and room for a unit
 * `scriptCityWeight`, and so does a place to found a city the pool can pay for. Its current hex wins ties, then the nearest, then insertion order, so
 * it never paces between equals. It walks there; it stays when it is already there.
 */
export function scriptedKing(state: RunState, faction: FactionId): RunState {
  const court = state.rules.court;
  const king = kingOf(state, faction);
  if (!court || !king) {
    return state;
  }
  const reach = kingReach(state, faction);
  // A soldier on a mayor's land is the mayor's while that land still has room to grow into.
  const delegated = (coord: AxialCoord): boolean => {
    const mayor = mayorOf(state, faction, coord);
    return mayor !== undefined && mayorLand(state, faction, mayor).length > 0;
  };
  const idleUnits = scriptUnitsOf(state, faction).filter(
    (unit) =>
      !unit.destination && !unit.commander && unit.squadOf === undefined && !delegated(unit.coord),
  );
  const hasRoom = unitCount(state, faction) < unitRoom(state, faction);
  const idleYards = hasRoom
    ? workingCities(state, faction).filter((city) => city.unitBuild === "none" && !city.mayor)
    : [];
  // Cities waiting for a mayor pull him home while a soldier he can send stands there (A32):
  // the capital trains them and gives the Will back fastest.
  const wanting = trust(state, faction) > 1 ? citiesWantingGovernor(state, faction).length : 0;
  const envoys = wanting > 0 ? soldiersAtHome(state, faction) : [];
  // So do commanders waiting for a target; off the capital he aims the ones in his reach.
  const unaimed = commandersOf(state, faction).filter((unit) => awaitsAim(state, unit));
  const aims = (from: AxialCoord): number =>
    hexEquals(from, king.capital) && capitalStands(state, faction)
      ? unaimed.length
      : unaimed.filter((unit) => hexDistance(unit.coord, from) <= reach).length;
  const sites = scriptCitySites(state, faction);
  const work = (from: AxialCoord): number =>
    (hexEquals(from, king.capital) && envoys.some((unit) => hexDistance(unit.coord, from) <= reach)
      ? wanting * court.scriptCityWeight
      : 0) +
    aims(from) * court.scriptCityWeight +
    (sites.some((hex) => hexDistance(hex.coord, from) <= reach) ? court.scriptCityWeight : 0) +
    idleUnits.filter((unit) => hexDistance(unit.coord, from) <= reach).length +
    idleYards.filter((city) => hexDistance(city.coord, from) <= reach).length *
      court.scriptCityWeight;
  const [best = king.coord] = rankBy(
    [king.coord, ...ownGround(state, faction).map((hex) => hex.coord)],
    (coord) => -work(coord),
    (coord) => hexDistance(coord, king.coord),
  );
  return hexEquals(best, king.coord) ? stopKing(state, faction) : moveKing(state, faction, best);
}

/**
 * The script's structures, on its build cadence (the enemy's; the fairness mirror runs it for
 * the player): its first hex that can found a city on land none of its cities reach founds
 * one; otherwise, with `improvements.scriptDevelopSupply` or more in the pool, its first hex
 * that can take an improvement gets one (A54: the King's own hand, a mayor being rare);
 * otherwise its first plain hex that can pay for a tower and touches anything not its own
 * raises one. Insertion order; no look-ahead, no memory, no randomness.
 */
export function scriptedBuild(state: RunState, faction: FactionId): RunState {
  const first = (accept: (hex: HexState) => boolean): HexState | undefined =>
    state.hexes.find((hex) => hex.owner === faction && accept(hex));
  const citySite = first(
    (hex) => isBuildable(state, faction, hex.coord, "city") && !inCityReach(state, hex.coord),
  );
  if (citySite) {
    return buildStructure(state, faction, citySite.coord, "city");
  }
  const floor = state.rules.improvements?.scriptDevelopSupply ?? 0;
  if (floor > 0 && supplyOf(state, faction) >= floor) {
    const kind = nextImprovement(state, ownedBy(state, faction));
    const site = first((hex) => isBuildable(state, faction, hex.coord, kind));
    if (site) {
      return buildStructure(state, faction, site.coord, kind);
    }
  }
  const towerSite = first(
    (hex) =>
      isBuildable(state, faction, hex.coord, "tower") &&
      hexNeighbors(hex.coord).some((neighbour) => {
        const near = findHex(state, neighbour);
        return near !== undefined && !near.razed && !allied(state.rules, near.owner, faction);
      }),
  );
  return towerSite ? buildStructure(state, faction, towerSite.coord, "tower") : state;
}

/** Own working cities out of the King's reach with no mayor and nobody on the way to the office. */
function citiesWantingGovernor(state: RunState, faction: FactionId): HexState[] {
  const cities = workingCities(state, faction);
  // A one-city King walks home instead: a soldier is too dear to spend on his only city.
  if (cities.length < 2) {
    return [];
  }
  return cities.filter(
    (city) =>
      !inKingReach(state, faction, city.coord) && awaitsGovernor(state, faction, city.coord),
  );
}

/** Soldiers the script would hand an office to: the King's own, in his reach, on no errand, not busy taking land. */
function governorCandidates(state: RunState, faction: FactionId): Unit[] {
  return soldiersAtHome(state, faction).filter((unit) => inKingReach(state, faction, unit.coord));
}

/** The King's own soldiers on his own land, under no commander and on no errand: the ones he could send to govern. */
function soldiersAtHome(state: RunState, faction: FactionId): Unit[] {
  return scriptUnitsOf(state, faction).filter(
    (unit) =>
      !unit.commander &&
      unit.squadOf === undefined &&
      !unit.governs &&
      findHex(state, unit.coord)?.owner === faction,
  );
}

/** Own plain land no city reaches yet where the script would found a city, while the pool holds its price. */
function scriptCitySites(state: RunState, faction: FactionId): HexState[] {
  if (supplyOf(state, faction) < state.rules.cityCost) {
    return [];
  }
  return ownGround(state, faction).filter(
    (hex) => isCitySite(state, faction, hex.coord) && !inCityReach(state, hex.coord),
  );
}

/**
 * The script's King sends a soldier to govern every own working city his reach does not
 * cover, while the court lets him: of the soldiers at hand the one nearest the city, then the
 * lower id, and only where a road exists.
 */
export function scriptedAppointments(state: RunState, faction: FactionId): RunState {
  let next = state;
  const army = state.rules.court?.scriptGovernorArmy ?? 0;
  for (const city of citiesWantingGovernor(state, faction)) {
    // The office costs the soldier (A32): a small army has nobody to spare.
    if (unitsOf(next, faction).length < army) {
      break;
    }
    const ranked = rankBy(
      governorCandidates(next, faction),
      (soldier) => hexDistance(soldier.coord, city.coord),
      (soldier) => soldier.id,
    );
    next = applyFirst(next, ranked, (from, soldier) =>
      appointGovernor(from, faction, soldier.id, city.coord),
    );
  }
  return next;
}

/**
 * A47: with `lords.scriptMusters` the script tells its mayors to muster once it is a kingdom
 * of `scriptCitiesPerCommander` working cities, one stance a turn while the Will lasts.
 * A54: with `improvements.scriptDevelopSupply` it tells a guarding mayor to develop once the
 * pool holds that much and his land has a site, and back to guard once it has none.
 */
export function scriptedStances(state: RunState, faction: FactionId): RunState {
  const court = state.rules.court;
  if (!court) {
    return state;
  }
  if (
    court.lords.scriptMusters &&
    workingCities(state, faction).length >= court.scriptCitiesPerCommander
  ) {
    const mustering = applyFirst(state, mayoredCities(state, faction), (from, city) =>
      setStance(from, faction, city.coord, "muster"),
    );
    if (mustering !== state) {
      return mustering;
    }
  }
  const floor = state.rules.improvements?.scriptDevelopSupply ?? 0;
  if (floor <= 0) {
    return state;
  }
  return applyFirst(state, mayoredCities(state, faction), (from, city) => {
    const stance = stanceOf(from, city.coord);
    const sites = developSites(from, faction, city).length > 0;
    const wanted: MayorStance | undefined =
      stance === "garrison" && sites && supplyOf(from, faction) >= floor
        ? "develop"
        : stance === "develop" && !sites
          ? "garrison"
          : undefined;
    return wanted ? setStance(from, faction, city.coord, wanted) : from;
  });
}

/**
 * The script's turn (the enemy's; the fairness mirror runs it for the player): appoint
 * mayors where its King cannot reach, promote and aim commanders, recruit in every idle city, send every idle unit
 * somewhere, then walk the King to his work. No look-ahead, no memory, no
 * randomness: nothing derived from earlier player actions is stored or expressed.
 * A66: with `company.scriptBanners` the banner of his squads comes first, and a King short of
 * its price gives no other command that turn: the pips he spends as they come never add up to
 * a banner (measured: the pool holds the price on 3 % of his turns with a commander alive).
 */
export function scriptedTurn(state: RunState, faction: FactionId): RunState {
  if (waitsForBanner(state, faction)) {
    return scriptedKing(state, faction);
  }
  const bannered = scriptedBanner(state, faction);
  const appointed = scriptedAims(
    scriptedPromotions(scriptedStances(scriptedAppointments(bannered, faction), faction), faction),
    faction,
  );
  const commanded = scriptSaves(appointed, faction)
    ? appointed
    : scriptedCommands(scriptedCall(appointed, faction), faction);
  return scriptedKing(commanded, faction);
}

/**
 * The script holds its one-pip commands back while a two-pip one is within its grasp: a city
 * waiting for a mayor with a soldier at hand to send while the pool is short, or a city
 * site in his reach that the supply can pay for (the build cadence founds it), or a soldier
 * in his reach waiting for the badge. Without this
 * it spends every pip the moment it arrives and never delegates or founds (Increment 5.2
 * report: 0 to 1 mayors a run).
 */
function scriptSaves(state: RunState, faction: FactionId): boolean {
  const court = state.rules.court;
  const king = kingOf(state, faction);
  if (!court || !king) {
    return false;
  }
  const wantsMayor =
    king.will < court.costs.appoint &&
    trust(state, faction) > 1 &&
    citiesWantingGovernor(state, faction).length > 0 &&
    governorCandidates(state, faction).length > 0;
  const wantsCity = scriptCitySites(state, faction).some((hex) =>
    inKingReach(state, faction, hex.coord),
  );
  const wantsCommander =
    king.will < court.costs.promote &&
    trust(state, faction) > 1 &&
    promotionCandidate(state, faction) !== undefined;
  return wantsMayor || wantsCity || wantsCommander;
}
